import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';

import type { PhotoEditorProps, PhotoEditorViewModel, ZoneConfig, ZoneImageState } from './types';
import type { DoodleCanvasRef } from './ui/DoodleCanvas';

import { validateImageFile } from './utils/fileUtils';
import { createEditorHandlers } from './utils/editorHandlers';
import { createSaveHandler } from './utils/saveHandler';
import { calculatePNGMaskPositioning, applyPNGMaskToElement } from './utils/pngProcessor';
import { detectStability } from './utils/stabilityDetector';
import { getElementDimensions, validateDimensions } from './utils/dimensionUtils';
import { createKeyDownHandler } from './utils/otherHandlers';
import { colorNameToHex } from './utils/colorUtils';

import { useBlobUrl } from './hooks/useBlobUrl';
import { useImageProcessing } from './hooks/useImageProcessing';
import { useCanvasPreview } from './hooks/useCanvasPreview';

import themeStyles from './photoEditorTheme.module.css';

// Type for refs to avoid using 'as' assertions
type Ref<T> = { current: T };

// Type for file element events to avoid using React.ChangeEvent
type FileElementEvent<T extends HTMLElement = HTMLElement> = {
  target: T & { files?: FileList | null };
};

// Helper function to create properly typed refs
// Note: This creates a plain object ref, not a React ref
// Use useRef() directly at the top level of hooks for React refs
function createRef<T>(initialValue: T): Ref<T> {
  return { current: initialValue };
}

export const useViewModel = ({
  onFileSelect,
  className = '',
  disabled = false,
  buttonVariant = 'primary',
  buttonColor = 'orange',
  buttonSize = 'small',
  maxFileSize,
  selectedFile = null,
  autoOpen = false,
  stepImageUrl = null,
  uploadType = 'photo',
  maskSvgBlob = null,
  positioning,
  maskImageConfig,
  showStarMapSlot = false,
  showLocationMapSlot = false,
  resetScaleTrigger,
  maskZones,
  doodle = false,
  overlayImageUrl
}: PhotoEditorProps): PhotoEditorViewModel => {
  // Determine if we're in multi-zone mode
  const isMultiZoneMode = maskZones && maskZones.length > 0;

  // Initialize zones from maskZones prop
  const zones: ZoneConfig[] = useMemo(() => {
    if (!maskZones || maskZones.length === 0) {
      return [];
    }
    return maskZones.map((zone, index) => ({
      zoneLabel: zone.zoneLabel,
      color: zone.colour,
      colorHex: colorNameToHex(zone.colour),
      index
    }));
  }, [maskZones]);
  // State management for modal visibility and image transformations
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const isEditorOpenRef = useRef(false);
  const [selectedImage, setSelectedImage]: [File | null, (value: File | null) => void] = useState(selectedFile);
  const [imageUrl, setImageUrl]: [string | null, (value: string | null) => void] = useState(null);
  const [rotation, setRotation] = useState(positioning?.rotation || 0);
  const [scale, setScale] = useState(positioning?.scale || 1);
  const [flipX, setFlipX] = useState(false);
  // Image offsets always start at 0 (positioning.x/y are mask positions, not image offsets)
  // For star maps, offsets remain at 0 to center the image with the mask
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedSrc, setProcessedSrc]: [string | null, (value: string | null) => void] = useState(null);
  const [stepImageDimensions, setStepImageDimensions]: [
    { width: number; height: number } | null,
    (value: { width: number; height: number } | null) => void
  ] = useState(null);
  const [canvasPreview, setCanvasPreview]: [string | null, (value: string | null) => void] = useState(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [isMaskReady, setIsMaskReady] = useState(false);

  // Multi-zone state
  const [zoneImages, setZoneImages]: [Map<number, ZoneImageState>, (value: Map<number, ZoneImageState>) => void] =
    useState(new Map());
  const [activeZoneIndex, setActiveZoneIndex] = useState(0);
  const [activeZoneGlow, setActiveZoneGlow] = useState(false);

  // Doodle state
  const [strokeWidth, setStrokeWidth] = useState(5);
  const [doodleCanvasDimensions, setDoodleCanvasDimensions] = useState({ width: 0, height: 0 });
  const doodleCanvasRef = useRef<DoodleCanvasRef | null>(null);

  // Custom hooks for blob URL and image management
  const { createBlobUrl, revokeBlobUrl, revokeAllBlobUrls } = useBlobUrl();
  const { processImage: processImageHook } = useImageProcessing();
  const { generatePreview } = useCanvasPreview();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const stepImageRef = useRef<HTMLImageElement | null>(null);
  const hasAutoOpenedRef = useRef<boolean>(false);
  const previousSelectedFileRef = useRef<File | null>(null);
  const previousAutoOpenRef = useRef<boolean | undefined>(autoOpen);
  const isInitialMountRef = useRef(true);

  // Store refs in a Map keyed by zone index - best practice for dynamic refs
  // This ensures refs persist across renders and handle zone changes correctly
  // Using a Map allows us to efficiently manage refs for dynamic zone lists
  type FileInputRef = { current: HTMLInputElement | null };
  const fileInputRefsMapRef = useRef<Map<number, FileInputRef>>(new Map());

  // Create a stable array of refs that matches the zones array order
  // This memoized array ensures refs are properly managed and don't get stale
  // Note: We create plain object refs here (not useRef) because we can't call hooks inside useMemo
  // These refs work the same way as React refs for our use case
  const fileInputRefs = useMemo(() => {
    const refsMap = fileInputRefsMapRef.current;

    // Create refs for any new zones that don't have refs yet
    zones.forEach((zone) => {
      if (!refsMap.has(zone.index)) {
        // Create a plain object ref (compatible with React.RefObject interface)
        refsMap.set(zone.index, { current: null });
      }
    });

    // Remove refs for zones that no longer exist
    const currentZoneIndices = new Set(zones.map((zone) => zone.index));
    for (const [index] of refsMap) {
      if (!currentZoneIndices.has(index)) {
        refsMap.delete(index);
      }
    }

    // Return array of refs in the same order as zones
    // Type assertion is safe because we just ensured all zones have refs
    // FileInputRef is compatible with React.RefObject (both have { current: T } shape)
    return zones.map((zone) => refsMap.get(zone.index)!) as unknown as React.RefObject<HTMLInputElement | null>[];
  }, [zones]);

  // Computed: check if all zones have files
  const allZonesHaveFiles = useMemo(() => {
    if (!isMultiZoneMode || zones.length === 0) {
      return true; // Single zone mode always returns true
    }
    return zones.every((zone) => zoneImages.has(zone.index));
  }, [isMultiZoneMode, zones, zoneImages]);

  // Wrapper to keep ref in sync with state
  const setIsEditorOpenWithRef = useCallback(
    (value: boolean) => {
      setIsEditorOpen(value);
      isEditorOpenRef.current = value;
    },
    [setIsEditorOpen]
  );

  // Restore preview when selectedFile is provided and auto-open editor if requested
  useEffect(() => {
    // On initial mount, if selectedFile is provided, we need to initialize
    // On subsequent renders, only act when selectedFile prop actually changes
    const selectedFileChanged = previousSelectedFileRef.current !== selectedFile;
    const needsInitialization = isInitialMountRef.current && selectedFile && selectedFile instanceof File;

    // Handle file initialization or changes
    if (selectedFileChanged || needsInitialization) {
      // Mark that we've completed the initial mount
      isInitialMountRef.current = false;

      // Update ref to track the current value
      previousSelectedFileRef.current = selectedFile;

      if (selectedFile && selectedFile instanceof File) {
        const preview = URL.createObjectURL(selectedFile);
        setImageUrl(preview);
        setSelectedImage(selectedFile);

        // For star maps and location maps, reset offsets to center the image with the mask
        const isMapImage =
          selectedFile.name === 'star-map.png' ||
          selectedFile.name === 'location-map.png' ||
          showStarMapSlot ||
          showLocationMapSlot;
        if (isMapImage) {
          setOffsetX(0);
          setOffsetY(0);
        }
      } else if (selectedFile === null) {
        // Only clear state when selectedFile prop explicitly changes from File → null
        // This prevents clearing when imageUrl was just set by handleFileSelect
        setImageUrl((currentUrl) => {
          if (currentUrl) {
            revokeBlobUrl(currentUrl);
          }
          return null;
        });
        setSelectedImage(null);
        hasAutoOpenedRef.current = false; // Reset flag when file is cleared
        isEditorOpenRef.current = false; // Reset ref when file is cleared
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }

    // Handle auto-open: check when autoOpen changes to true or when we have a file and autoOpen is true
    // This runs on every effect execution to catch when autoOpen changes
    if (
      autoOpen &&
      selectedFile &&
      selectedFile instanceof File &&
      !isEditorOpenRef.current &&
      !hasAutoOpenedRef.current
    ) {
      setIsEditorOpenWithRef(true);
      hasAutoOpenedRef.current = true;
    }

    // Update ref to track current autoOpen value
    previousAutoOpenRef.current = autoOpen;
  }, [selectedFile, revokeBlobUrl, autoOpen, showStarMapSlot, showLocationMapSlot, setIsEditorOpenWithRef]);

  // Keep ref in sync with state (for cases where state changes from other sources)
  useEffect(() => {
    isEditorOpenRef.current = isEditorOpen;
  }, [isEditorOpen]);

  // Reset mask ready state when editor opens/closes
  useEffect(() => {
    if (!isEditorOpen) {
      setIsMaskReady(false);
    } else if (maskSvgBlob && maskImageConfig?.url) {
      // Hide mask when editor opens (controlled via component inline styles)
      setIsMaskReady(false);
    }
  }, [isEditorOpen, maskSvgBlob, maskImageConfig]);

  // Clean up object URL when component unmounts or file changes
  // Skip image processing for star maps and location maps (they don't need contrast adjustment)
  useEffect(() => {
    // Check if current image is a star map or location map
    const isCurrentImageMap =
      selectedImage?.name === 'star-map.png' ||
      selectedImage?.name === 'location-map.png' ||
      showStarMapSlot ||
      showLocationMapSlot;

    // Only process if it's an engraving AND not a map image
    if (uploadType !== 'engraving' || !imageUrl || isCurrentImageMap) {
      // For map images, use the original image without processing
      if (isCurrentImageMap && imageUrl) {
        setProcessedSrc(imageUrl);
      }
      return;
    }

    setIsProcessing(true);
    processImageHook(imageUrl)
      .then((processed) => {
        setProcessedSrc(processed);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to process image');
        // Try to use fallback or original
        if (imageUrl) {
          setProcessedSrc(imageUrl);
        }
      })
      .finally(() => {
        setIsProcessing(false);
      });
  }, [uploadType, imageUrl, processImageHook, selectedImage, showStarMapSlot, showLocationMapSlot]);

  // Handle step image load to get dimensions
  const handleStepImageLoad = useCallback(() => {
    if (stepImageRef.current) {
      const updateDimensions = () => {
        if (stepImageRef.current) {
          // Get the natural dimensions of the image
          const naturalWidth = stepImageRef.current.naturalWidth;
          const naturalHeight = stepImageRef.current.naturalHeight;

          // Only proceed when we have natural dimensions
          if (naturalWidth > 0 && naturalHeight > 0) {
            // Use standardized dimensions (1250px width with calculated height)
            const standardWidth = 1250;
            const standardHeight = (standardWidth * naturalHeight) / naturalWidth;
            setStepImageDimensions({ width: standardWidth, height: standardHeight });
          }
        }
      };

      // Try immediately
      updateDimensions();
    }
  }, []);

  // Use a ref to track if we're already generating to prevent infinite loops
  const isGeneratingRef = useRef(false);

  // Generate preview when transformations change
  useEffect(() => {
    const shouldGeneratePreview =
      isEditorOpen &&
      !isGeneratingRef.current &&
      ((isMultiZoneMode && zoneImages.size > 0) || (!isMultiZoneMode && imageUrl));

    if (!shouldGeneratePreview) {
      return;
    }

    // Debounce preview generation to avoid excessive calls
    const timeoutId = setTimeout(async () => {
      if (!isGeneratingRef.current) {
        isGeneratingRef.current = true;

        try {
          if (!isEditorOpen || isGeneratingPreview) {
            return;
          }

          setIsGeneratingPreview(true);

          // Get container dimensions for scaling
          const container = document.querySelector('#photo-editor-canvas-container') as HTMLElement;
          const containerWidth = container?.offsetWidth || 400;
          const containerHeight = container?.offsetHeight || 400;

          if (isMultiZoneMode && zones.length > 0 && zoneImages.size > 0) {
            // Multi-zone mode: generate preview with all zones
            const previewDataUrl = await generatePreview({
              imageUrl: imageUrl || '', // Required but not used in multi-zone mode
              rotation: 0, // Not used in multi-zone mode
              scale: 1, // Not used in multi-zone mode
              flipX: false, // Not used in multi-zone mode
              offsetX: 0, // Not used in multi-zone mode
              offsetY: 0, // Not used in multi-zone mode
              stepImageDimensions,
              uploadType,
              processedSrc: null, // Not used in multi-zone mode
              maskSvgBlob,
              positioning,
              containerWidth,
              containerHeight,
              showStarMapSlot,
              zones,
              zoneImages,
              isMultiZoneMode: true,
              maskImageConfig
            });

            setCanvasPreview(previewDataUrl);
          } else if (!isMultiZoneMode && imageUrl) {
            // Single zone mode: existing behavior
            const isCurrentImageStarMap =
              selectedImage?.name === 'star-map.png' ||
              selectedImage?.name === 'location-map.png' ||
              showStarMapSlot ||
              showLocationMapSlot;

            const previewDataUrl = await generatePreview({
              imageUrl,
              rotation,
              scale,
              flipX,
              offsetX,
              offsetY,
              stepImageDimensions,
              uploadType,
              processedSrc,
              maskSvgBlob,
              positioning,
              containerWidth,
              containerHeight,
              showStarMapSlot: isCurrentImageStarMap
            });

            setCanvasPreview(previewDataUrl);
          }
        } catch (error) {
          console.error('Failed to generate canvas preview:', error);
        } finally {
          setIsGeneratingPreview(false);
          isGeneratingRef.current = false;
        }
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [
    isEditorOpen,
    isMultiZoneMode,
    imageUrl,
    rotation,
    scale,
    flipX,
    offsetX,
    offsetY,
    stepImageDimensions,
    uploadType,
    processedSrc,
    maskSvgBlob,
    positioning,
    showStarMapSlot,
    showLocationMapSlot,
    selectedImage,
    zones,
    zoneImages,
    maskImageConfig
  ]);

  // Persistent cleanup function that doesn't get reset on re-mount
  const persistentCleanupRef = useRef<(() => void) | null>(null);
  // Guard to prevent concurrent mask applications
  const isApplyingMaskRef = useRef(false);

  // Readiness tracking state
  type ReadinessState = {
    stepImageLoaded: boolean;
    containerFound: boolean;
    dimensionsValid: boolean;
    dimensionsStable: boolean;
    lastDimensions: { width: number; height: number } | null;
    maxDimensions: { width: number; height: number } | null;
    stableFrames: number;
    framesSinceMaxSize: number;
  };
  const readinessStateRef = useRef<ReadinessState>({
    stepImageLoaded: false,
    containerFound: false,
    dimensionsValid: false,
    dimensionsStable: false,
    lastDimensions: null,
    maxDimensions: null,
    stableFrames: 0,
    framesSinceMaxSize: 0
  });

  // Apply PNG mask when editor opens using comprehensive readiness tracking
  useEffect(() => {
    if (!isEditorOpen || !maskSvgBlob || !stepImageDimensions || !maskImageConfig?.url) {
      return;
    }

    const container = document.querySelector('#photo-editor-image-container') as HTMLElement;
    if (!container) {
      return;
    }

    // Reset readiness state
    readinessStateRef.current = {
      stepImageLoaded: false,
      containerFound: true,
      dimensionsValid: false,
      dimensionsStable: false,
      lastDimensions: null,
      maxDimensions: null,
      stableFrames: 0,
      framesSinceMaxSize: 0
    };

    // Hide mask initially (state is already false, but ensure container is hidden)
    setIsMaskReady(false);

    // Prevent concurrent mask applications
    if (isApplyingMaskRef.current) {
      return;
    }

    // Helper to check if step image is fully loaded
    const checkStepImageLoaded = (): boolean => {
      if (!stepImageRef.current) {
        return false;
      }
      const img = stepImageRef.current;
      const loaded = img.complete && img.naturalWidth > 0 && img.naturalHeight > 0;
      if (loaded !== readinessStateRef.current.stepImageLoaded) {
        readinessStateRef.current.stepImageLoaded = loaded;
      }
      return loaded;
    };

    // Set up image load listener for early detection
    const stepImage = stepImageRef.current;
    let imageLoadHandler: (() => void) | null = null;
    let imageErrorHandler: (() => void) | null = null;

    if (stepImage) {
      // Check if already loaded
      if (stepImage.complete && stepImage.naturalWidth > 0 && stepImage.naturalHeight > 0) {
        readinessStateRef.current.stepImageLoaded = true;
      } else {
        // Set up listeners for when image loads
        imageLoadHandler = () => {
          readinessStateRef.current.stepImageLoaded = true;
        };
        imageErrorHandler = () => {
          // Image failed to load - mark as loaded to prevent infinite waiting
          // The check will still validate naturalWidth/Height > 0
          console.warn('Step image failed to load');
        };
        stepImage.addEventListener('load', imageLoadHandler);
        stepImage.addEventListener('error', imageErrorHandler);
      }
    }

    // Calculate adaptive stability threshold based on container size
    // Use a stricter threshold: 0.5px absolute or 0.05% of container width, whichever is larger
    const getStabilityThreshold = (width: number): number => {
      return Math.max(0.5, Math.round(width * 0.0005));
    };

    // Helper to check all readiness conditions
    const checkReadiness = (dimensions: { width: number; height: number }): boolean => {
      const state = readinessStateRef.current;

      // Check step image (only check periodically, not every frame)
      if (!checkStepImageLoaded()) {
        return false;
      }

      // Use consistent validation with validateDimensions utility
      const valid = validateDimensions(dimensions);
      if (valid !== state.dimensionsValid) {
        state.dimensionsValid = valid;
      }
      if (!valid) {
        return false;
      }

      // Track maximum dimensions seen to ensure we're at or near the final size
      if (!state.maxDimensions) {
        state.maxDimensions = { ...dimensions };
      } else {
        const totalArea = dimensions.width * dimensions.height;
        const maxArea = state.maxDimensions.width * state.maxDimensions.height;

        // Update max if current is larger
        if (totalArea > maxArea) {
          state.maxDimensions = { ...dimensions };
          state.framesSinceMaxSize = 0;
        } else {
          state.framesSinceMaxSize++;
        }
      }

      // Check dimensions are stable with adaptive threshold
      if (state.lastDimensions) {
        const threshold = getStabilityThreshold(dimensions.width);
        const widthDiff = Math.abs(dimensions.width - state.lastDimensions.width);
        const heightDiff = Math.abs(dimensions.height - state.lastDimensions.height);
        const stable = widthDiff < threshold && heightDiff < threshold;

        if (stable) {
          state.stableFrames++;
        } else {
          state.stableFrames = 0;
          state.dimensionsStable = false;
        }

        // Update last dimensions for next comparison
        state.lastDimensions = dimensions;
      } else {
        // First measurement - initialize
        state.lastDimensions = dimensions;
        state.stableFrames = 0;
      }

      // Require all of the following:
      // 1. Step image loaded
      // 2. Dimensions valid
      // 3. Dimensions stable for at least 20 frames (~330ms at 60fps)
      // 4. We've been at or near max size for at least 10 frames (~165ms)
      // 5. Current dimensions are within 1% of max dimensions (to catch any final settling)
      const isNearMaxSize = state.maxDimensions
        ? Math.abs(dimensions.width - state.maxDimensions.width) / state.maxDimensions.width < 0.01 &&
          Math.abs(dimensions.height - state.maxDimensions.height) / state.maxDimensions.height < 0.01
        : false;

      if (state.stableFrames >= 20 && !state.dimensionsStable) {
        state.dimensionsStable = true;
      }

      // All conditions met
      return (
        state.stepImageLoaded &&
        state.dimensionsValid &&
        state.dimensionsStable &&
        isNearMaxSize &&
        state.framesSinceMaxSize >= 10
      );
    };

    let frameId: number | null = null;
    let startTime = Date.now();
    const maxWaitTime = 5000; // Increased to 5 seconds
    const minWaitTime = 400; // Minimum wait time to allow CSS transitions/animations to complete
    let cancelled = false;
    let imageCheckCounter = 0; // Only check image load every N frames

    const checkAndApply = () => {
      if (cancelled) {
        return;
      }

      const elapsed = Date.now() - startTime;
      const current = getElementDimensions(container);

      // Always wait minimum time to allow CSS transitions/animations to complete
      if (elapsed < minWaitTime) {
        frameId = requestAnimationFrame(checkAndApply);
        return;
      }

      // Check timeout - but still require all readiness conditions to be met
      if (elapsed > maxWaitTime) {
        // On timeout, still require all conditions (including stability) to be met
        // This prevents applying mask prematurely
        if (validateDimensions(current) && checkReadiness(current)) {
          applyMask(current);
        } else {
          // As a last resort, apply with current dimensions if they're valid
          // This prevents the mask from never showing
          if (validateDimensions(current) && readinessStateRef.current.stepImageLoaded) {
            console.warn('Applying mask with timeout dimensions as fallback');
            applyMask(current);
          }
        }
        return;
      }

      if (!validateDimensions(current)) {
        frameId = requestAnimationFrame(checkAndApply);
        return;
      }

      // Only check image load every 10 frames (optimization)
      // Since we have event listeners, this is just a fallback check
      if (imageCheckCounter % 10 === 0) {
        checkStepImageLoaded();
      }
      imageCheckCounter++;

      // Check if all readiness conditions are met (this will update lastDimensions internally)
      if (checkReadiness(current)) {
        // Double-check dimensions one more time before applying
        // This ensures we're using the most recent dimensions
        const finalCheck = getElementDimensions(container);
        if (!cancelled && !isApplyingMaskRef.current) {
          applyMask(finalCheck);
        }
        return;
      }

      // Continue checking
      frameId = requestAnimationFrame(checkAndApply);
    };

    const applyMask = async (dimensions: { width: number; height: number }) => {
      if (isApplyingMaskRef.current) {
        return;
      }

      isApplyingMaskRef.current = true;

      try {
        // Calculate mask positioning
        const maskPositioning = calculatePNGMaskPositioning({
          containerWidth: dimensions.width,
          containerHeight: dimensions.height,
          positioning
        });

        // Clean up existing mask
        if (persistentCleanupRef.current) {
          persistentCleanupRef.current();
          persistentCleanupRef.current = null;
        }

        // Apply new mask
        const cleanup = await applyPNGMaskToElement(container, maskImageConfig.url, maskPositioning, maskImageConfig);

        if (cleanup) {
          persistentCleanupRef.current = cleanup;
          // Show mask now that it's in final position (controlled via React state)
          setIsMaskReady(true);
        }
      } catch (error) {
        console.warn('Failed to apply mask:', error);
      } finally {
        isApplyingMaskRef.current = false;
      }
    };

    // Start checking
    frameId = requestAnimationFrame(checkAndApply);

    return () => {
      cancelled = true;
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      // Clean up image event listeners
      if (stepImage && imageLoadHandler) {
        stepImage.removeEventListener('load', imageLoadHandler);
      }
      if (stepImage && imageErrorHandler) {
        stepImage.removeEventListener('error', imageErrorHandler);
      }
      isApplyingMaskRef.current = false;
    };
  }, [isEditorOpen, maskSvgBlob, stepImageDimensions, positioning, maskImageConfig]);

  // Clean up mask when editor closes
  useEffect(() => {
    if (!isEditorOpen) {
      if (persistentCleanupRef.current) {
        persistentCleanupRef.current();
        persistentCleanupRef.current = null;
      }
      setIsMaskReady(false);
    }
  }, [isEditorOpen]);

  // Handle container resize events with RAF-based stability detection
  useEffect(() => {
    if (!isEditorOpen || !maskSvgBlob || !stepImageDimensions || !maskImageConfig?.url) {
      return;
    }

    const container = document.querySelector('#photo-editor-image-container') as HTMLElement;
    if (!container) {
      return;
    }

    // Track previous positioning to avoid unnecessary reapplications
    let previousPositioning: { maskCenterX: number; maskCenterY: number; maskScalePercent: number } | null = null;
    let resizeTimeout: NodeJS.Timeout | null = null;
    let cleanupDetector: (() => void) | null = null;

    const handleResize = () => {
      // Clear existing timeout and detector
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      if (cleanupDetector) {
        cleanupDetector();
        cleanupDetector = null;
      }

      // Debounce resize handling
      resizeTimeout = setTimeout(() => {
        // Prevent concurrent mask applications
        if (isApplyingMaskRef.current) {
          return;
        }

        // Use stability detection to wait for stable dimensions
        cleanupDetector = detectStability(
          container,
          async (dimensions) => {
            // Double-check guard
            if (isApplyingMaskRef.current) {
              return;
            }

            // Calculate new positioning
            const maskPositioning = calculatePNGMaskPositioning({
              containerWidth: dimensions.width,
              containerHeight: dimensions.height,
              positioning
            });

            // Check if positioning has changed significantly
            if (previousPositioning) {
              const positioningChanged =
                Math.abs(maskPositioning.maskCenterX - previousPositioning.maskCenterX) > 5 ||
                Math.abs(maskPositioning.maskCenterY - previousPositioning.maskCenterY) > 5 ||
                Math.abs(maskPositioning.maskScalePercent - previousPositioning.maskScalePercent) > 0.01;

              if (!positioningChanged) {
                return;
              }
            }

            isApplyingMaskRef.current = true;

            try {
              // Clean up existing mask
              if (persistentCleanupRef.current) {
                persistentCleanupRef.current();
                persistentCleanupRef.current = null;
              }

              // Apply new mask
              const cleanup = await applyPNGMaskToElement(
                container,
                maskImageConfig.url,
                maskPositioning,
                maskImageConfig
              );

              if (cleanup) {
                persistentCleanupRef.current = cleanup;
              }

              // Update previous positioning
              previousPositioning = maskPositioning;
            } catch (error) {
              console.warn('Failed to reapply mask on resize:', error);
            } finally {
              isApplyingMaskRef.current = false;
            }
          },
          {
            requiredFrames: 5, // Increased for more stability on resize
            threshold: 2, // Stricter threshold
            maxWaitTime: 2000
          }
        );
      }, 300);
    };

    // Use ResizeObserver to detect container size changes
    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      if (cleanupDetector) {
        cleanupDetector();
      }
    };
  }, [isEditorOpen, maskSvgBlob, stepImageDimensions, positioning, maskImageConfig]);

  /**
   * Handles file selection from input
   * Creates preview URL and opens editor modal
   * In multi-zone mode, stores file per zone and opens modal when all zones have files
   */
  const handleFileSelect = useCallback(
    async (event: FileElementEvent<HTMLInputElement>, zoneIndex: number = 0) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Reset error state
      setError('');
      setIsLoading(true);

      try {
        // Validate the file
        if (!validateImageFile(file, maxFileSize, setError)) {
          return;
        }

        if (isMultiZoneMode) {
          // Multi-zone mode: store file per zone
          const url = createBlobUrl(file);

          // Process image if needed (for engraving)
          let processedSrcValue: string | null = null;
          if (uploadType === 'engraving') {
            try {
              processedSrcValue = await processImageHook(url);
            } catch {
              processedSrcValue = url; // Fallback to original
            }
          } else {
            processedSrcValue = url;
          }

          // Update zone image state
          setZoneImages((prev) => {
            const newMap = new Map(prev);
            newMap.set(zoneIndex, {
              file,
              blobUrl: url,
              rotation: 0,
              scale: 1,
              flipX: false,
              offsetX: 0,
              offsetY: 0,
              processedSrc: processedSrcValue
            });
            return newMap;
          });

          // Check if all zones now have files, and if so, open modal
          setZoneImages((current) => {
            const allHaveFiles = zones.every((zone) => current.has(zone.index) || zone.index === zoneIndex);
            if (allHaveFiles && !isEditorOpenRef.current) {
              // Small delay to ensure state is updated
              setTimeout(() => {
                setIsEditorOpenWithRef(true);
                setActiveZoneIndex(0); // Set first zone as active
              }, 100);
            }
            return current;
          });
        } else {
          // Single zone mode: use existing behavior
          setSelectedImage(file);
          const url = createBlobUrl(file);
          setImageUrl(url);
          setIsEditorOpenWithRef(true);
          // Reset transformations for new image
          setRotation(0);
          setScale(1);
          setFlipX(false);
          setOffsetX(0);
          setOffsetY(0);
        }
      } catch {
        setError('Failed to process the image. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    [maxFileSize, isMultiZoneMode, zones, createBlobUrl, processImageHook, uploadType, setIsEditorOpenWithRef]
  );

  // Helper function to create unified setters that work in both single and multi-zone modes.
  type SetStateAction<T> = T | ((prev: T) => T);
  const createUnifiedSetter = <T extends number | boolean>(
    property: keyof Pick<ZoneImageState, 'rotation' | 'scale' | 'flipX' | 'offsetX' | 'offsetY'>,
    singleStateSetter: (value: SetStateAction<T>) => void,
    currentSingleValue: T
  ): ((value: SetStateAction<T>) => void) => {
    return (value: SetStateAction<T>) => {
      if (isMultiZoneMode) {
        setZoneImages((prev) => {
          const newMap = new Map(prev);
          const current = newMap.get(activeZoneIndex);
          if (current) {
            const currentValue = current[property] as T;
            const newValue = typeof value === 'function' ? (value as (prev: T) => T)(currentValue) : value;
            newMap.set(activeZoneIndex, {
              ...current,
              [property]: newValue
            });
          }
          return newMap;
        });
      } else {
        singleStateSetter(typeof value === 'function' ? (value as (prev: T) => T)(currentSingleValue) : value);
      }
    };
  };

  // Create editor handlers - in multi-zone mode, these affect the active zone
  const editorHandlersBase = createEditorHandlers(
    createUnifiedSetter('rotation', setRotation, rotation),
    createUnifiedSetter('scale', setScale, scale),
    createUnifiedSetter('flipX', setFlipX, flipX),
    createUnifiedSetter('offsetX', setOffsetX, offsetX),
    createUnifiedSetter('offsetY', setOffsetY, offsetY)
  );

  const isLocationMapEditor = showLocationMapSlot || selectedImage?.name === 'location-map.png';

  // Override handlers for multi-zone + location map behavior
  const editorHandlers = {
    ...editorHandlersBase,
    handleZoomOut: () => {
      if (isLocationMapEditor) {
        return;
      }
      editorHandlersBase.handleZoomOut();
    },
    handleZoomIn: () => {
      if (isLocationMapEditor) {
        return;
      }
      editorHandlersBase.handleZoomIn();
    },
    handleResetAll: () => {
      if (isMultiZoneMode) {
        setZoneImages((prev) => {
          const newMap = new Map(prev);
          prev.forEach((state, index) => {
            newMap.set(index, {
              ...state,
              rotation: 0,
              // Keep map scale fixed for location map flows (zoom is controlled in Mapbox).
              scale: isLocationMapEditor ? state.scale : 1,
              flipX: false,
              offsetX: 0,
              offsetY: 0
            });
          });
          return newMap;
        });
      } else {
        if (isLocationMapEditor) {
          setRotation(0);
          setFlipX(false);
          setOffsetX(0);
          setOffsetY(0);
          return;
        }
        editorHandlersBase.handleResetAll();
      }
    }
  };

  // Create save handler
  // Pass showStarMapSlot flag - the handler will check filename at save time
  const handleSave = createSaveHandler({
    imageUrl,
    selectedImage,
    rotation,
    scale,
    flipX,
    offsetX,
    offsetY,
    zones: isMultiZoneMode ? zones : undefined,
    zoneImages: isMultiZoneMode ? zoneImages : undefined,
    isMultiZoneMode,
    onFileSelect,
    stepImageDimensions,
    uploadType,
    maskSvgBlob,
    maskImageConfig,
    setIsProcessing,
    setIsEditorOpen,
    setImageUrl,
    setError,
    positioning,
    showStarMapSlot,
    doodle,
    doodleCanvasRef
  });

  // Zone button click handler
  const handleZoneButtonClick = useCallback((zoneIndex: number) => {
    setActiveZoneIndex(zoneIndex);
    // Trigger glow effect
    setActiveZoneGlow(true);
    setTimeout(() => setActiveZoneGlow(false), 1000);
  }, []);

  // Doodle handlers
  const handleStrokeWidthChange = useCallback((width: number) => {
    setStrokeWidth(width);
  }, []);

  const handleDoodleClear = useCallback(() => {
    doodleCanvasRef.current?.clear();
  }, []);

  // Measure container dimensions for doodle canvas when modal opens
  useEffect(() => {
    if (isEditorOpen && doodle) {
      // Use a small delay to ensure the modal has rendered
      const measureContainer = () => {
        const container = document.querySelector('#photo-editor-image-container') as HTMLElement;
        if (container) {
          const rect = container.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            setDoodleCanvasDimensions({ width: rect.width, height: rect.height });
          }
        }
      };

      // Initial measurement after a short delay
      const timeoutId = setTimeout(measureContainer, 100);

      // Also set up a resize observer for responsive behavior
      const container = document.querySelector('#photo-editor-image-container') as HTMLElement;
      let resizeObserver: ResizeObserver | null = null;

      if (container) {
        resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            const { width, height } = entry.contentRect;
            if (width > 0 && height > 0) {
              setDoodleCanvasDimensions({ width, height });
            }
          }
        });
        resizeObserver.observe(container);
      }

      return () => {
        clearTimeout(timeoutId);
        resizeObserver?.disconnect();
      };
    }
  }, [isEditorOpen, doodle]);

  // Create other handlers
  const handleClose = useCallback(() => {
    const dialog = document.querySelector('.photo-editor__modal') as HTMLDialogElement;
    if (dialog && dialog.open) {
      dialog.close();
    }
    setIsEditorOpenWithRef(false);
    if (isMultiZoneMode) {
      // Clean up all zone blob URLs
      zoneImages.forEach((state) => {
        revokeBlobUrl(state.blobUrl);
      });
    } else {
      if (imageUrl) {
        revokeBlobUrl(imageUrl);
        setImageUrl(null);
      }
    }
  }, [isMultiZoneMode, zoneImages, imageUrl, revokeBlobUrl, setIsEditorOpenWithRef]);

  const handleClear = useCallback(
    (zoneIndex?: number) => {
      if (isMultiZoneMode && zoneIndex !== undefined) {
        // Clear specific zone
        setZoneImages((prev) => {
          const newMap = new Map(prev);
          const state = newMap.get(zoneIndex);
          if (state) {
            revokeBlobUrl(state.blobUrl);
            newMap.delete(zoneIndex);
          }
          if (fileInputRefs[zoneIndex]?.current) {
            fileInputRefs[zoneIndex].current!.value = '';
          }
          return newMap;
        });
      } else if (!isMultiZoneMode) {
        // Single zone mode
        setImageUrl('');
        setError('');
        setSelectedImage(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        if (onFileSelect) {
          onFileSelect(null);
        }
      }
    },
    [isMultiZoneMode, fileInputRefs, revokeBlobUrl, onFileSelect]
  );

  const handleButtonClick = useCallback(
    (zoneIndex?: number) => {
      if (isMultiZoneMode && typeof zoneIndex === 'number') {
        const ref = fileInputRefs[zoneIndex];
        if (!disabled && ref?.current) {
          ref.current.click();
        }
      } else if (!isMultiZoneMode) {
        if (!disabled && fileInputRef.current) {
          fileInputRef.current.click();
        }
      }
    },
    [disabled, isMultiZoneMode, fileInputRefs]
  );

  const handleKeyDown = createKeyDownHandler(isEditorOpen, handleClose);

  // Add escape key listener when modal is open
  useEffect(() => {
    if (isEditorOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isEditorOpen, handleKeyDown]);

  // Reset scale to fit mask after 300ms when resetScaleTrigger changes.
  // For star map images ONLY, calculate scale to fit the mask dimensions and center to the mask.
  useEffect(() => {
    if (resetScaleTrigger !== undefined) {
      const timeoutId = setTimeout(async () => {
        // Check if current image is a map (by filename or flag)
        const isCurrentImageMap =
          selectedImage?.name === 'star-map.png' ||
          selectedImage?.name === 'location-map.png' ||
          showStarMapSlot ||
          showLocationMapSlot;

        // Only apply special centering/scaling logic for map images
        if (isCurrentImageMap && imageUrl && maskImageConfig && positioning && stepImageDimensions) {
          try {
            // Load the image to get its dimensions
            const img = new Image();
            img.crossOrigin = 'anonymous';

            await new Promise<void>((resolve, reject) => {
              img.onload = () => resolve();
              img.onerror = () => reject(new Error('Failed to load image for scale calculation'));
              img.src = processedSrc || imageUrl;
            });

            const imageWidth = img.naturalWidth;
            const imageHeight = img.naturalHeight;

            // Canvas dimensions (standardized to 1250px width)
            const canvasWidth = 1250;
            const canvasHeight = (canvasWidth * stepImageDimensions.height) / stepImageDimensions.width;

            // Calculate how the image would be scaled to fit the canvas (base scaling)
            const imageAspectRatio = imageWidth / imageHeight;
            const canvasAspectRatio = canvasWidth / canvasHeight;

            let baseScaledWidth: number;
            let baseScaledHeight: number;

            if (imageAspectRatio > canvasAspectRatio) {
              // Image is wider - constrain by width to fit within canvas
              baseScaledWidth = canvasWidth;
              baseScaledHeight = canvasWidth / imageAspectRatio;
            } else {
              // Image is taller - constrain by height to fit within canvas
              baseScaledHeight = canvasHeight;
              baseScaledWidth = canvasHeight * imageAspectRatio;
            }

            // Calculate mask dimensions in canvas coordinates using maskImageConfig dimensions
            // The mask scale is a percentage of the canvas width (see applyPNGMaskClipping)
            const maskScalePercent = (positioning.scale || 100) / 100;
            const maskWidth = canvasWidth * maskScalePercent;
            // Use maskImageConfig dimensions directly to calculate height
            const maskHeight = maskWidth * (maskImageConfig.height / maskImageConfig.width);

            // Calculate what scale multiplier is needed so the image covers the mask
            // The final rendered size is baseScaledWidth * scale and baseScaledHeight * scale
            // We need both dimensions to be >= mask dimensions to cover the mask
            const scaleX = maskWidth / baseScaledWidth;
            const scaleY = maskHeight / baseScaledHeight;

            // Use the larger scale to ensure the image covers the entire mask (like object-fit: cover)
            const scaleToFitMask = Math.max(scaleX, scaleY);

            // Set the calculated scale
            setScale(scaleToFitMask);

            // Reset offsets to 0 to center the star map image with the mask (not the container)
            setOffsetX(0);
            setOffsetY(0);
          } catch (error) {
            console.error('Failed to calculate scale to fit mask, falling back to scale 1:', error);
            // Fallback to scale 1 if calculation fails
            setScale(1);
            // Still center the star map image with the mask
            setOffsetX(0);
            setOffsetY(0);
          }
        } else {
          // Default behavior for non-star-map images: reset to scale 1
          // Don't reset offsets for regular images (they may have been positioned by user)
          setScale(1);
        }
      }, 300);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [
    resetScaleTrigger,
    showStarMapSlot,
    showLocationMapSlot,
    imageUrl,
    maskImageConfig,
    positioning,
    processedSrc,
    stepImageDimensions,
    selectedImage
  ]);

  // Clean up blob URLs when component unmounts
  useEffect(() => {
    return () => {
      revokeAllBlobUrls();
    };
  }, [revokeAllBlobUrls]);

  // Build button classes based on variant and color
  const buttonClasses = [
    'button',
    `button--${buttonVariant}`,
    `button--${buttonColor}`,
    `button--${buttonSize}`,
    themeStyles['photo-editor__input-button'],
    className
  ]
    .filter(Boolean)
    .join(' ');

  // Zone button classes function
  const zoneButtonClasses = useCallback(
    (zoneIndex: number) => {
      const baseClasses = [themeStyles['photo-editor__zone-button'], 'button', 'button--secondary', 'button--small'];
      if (zoneIndex === activeZoneIndex) {
        baseClasses.push(themeStyles['photo-editor__zone-button--active']);
      }
      return baseClasses.filter(Boolean).join(' ');
    },
    [activeZoneIndex, themeStyles]
  );

  // Get current zone's transform values for display (in multi-zone mode)
  const currentRotation = isMultiZoneMode ? (zoneImages.get(activeZoneIndex)?.rotation ?? 0) : rotation;
  const currentScale = isMultiZoneMode ? (zoneImages.get(activeZoneIndex)?.scale ?? 1) : scale;
  const currentFlipX = isMultiZoneMode ? (zoneImages.get(activeZoneIndex)?.flipX ?? false) : flipX;

  // Compute overlay position to match the mask bounding box exactly.
  // Uses the same coordinate system as calculatePNGMaskPositioning / applyPNGMaskToElement.
  const overlayStyle = useMemo<React.CSSProperties | null>(() => {
    if (!overlayImageUrl || !positioning || !maskImageConfig || !stepImageDimensions) return null;

    const containerW = stepImageDimensions.width;
    const containerH = stepImageDimensions.height;
    if (containerW <= 0 || containerH <= 0) return null;

    const maskScalePercent = positioning.scale / 100;
    const maskAreaWidth = containerW * maskScalePercent;
    const maskAspectRatio = maskImageConfig.height / maskImageConfig.width;
    const maskAreaHeight = maskAreaWidth * maskAspectRatio;

    const maskCenterX = (containerW / 100) * positioning.x;
    const maskCenterY = (containerH / 100) * positioning.y;

    const leftPct = ((maskCenterX - maskAreaWidth / 2) / containerW) * 100;
    const topPct = ((maskCenterY - maskAreaHeight / 2) / containerH) * 100;
    const widthPct = (maskAreaWidth / containerW) * 100;
    const heightPct = (maskAreaHeight / containerH) * 100;

    return {
      position: 'absolute' as const,
      left: `${leftPct}%`,
      top: `${topPct}%`,
      width: `${widthPct}%`,
      height: `${heightPct}%`,
      objectFit: 'contain' as const,
      pointerEvents: 'none' as const,
      zIndex: 20
    };
  }, [overlayImageUrl, positioning, maskImageConfig, stepImageDimensions]);

  return {
    // State
    isEditorOpen,
    selectedImage,
    imageUrl,
    rotation: currentRotation,
    scale: currentScale,
    flipX: currentFlipX,
    offsetX,
    offsetY,
    error,
    isLoading,
    isProcessing,
    processedSrc,
    maskImageData: maskImageConfig?.url || null,
    stepImageDimensions,
    canvasPreview,
    isGeneratingPreview,
    showStarMapSlot,
    showLocationMapSlot,
    isMaskReady,

    // Multi-zone state
    zones,
    zoneImages,
    activeZoneIndex,
    allZonesHaveFiles,
    fileInputRefs,
    activeZoneGlow,

    // Doodle state
    doodle,
    strokeWidth,
    doodleCanvasDimensions,
    doodleCanvasRef,

    // Refs
    fileInputRef,
    stepImageRef,

    // Handlers
    handleFileSelect,
    handleSave,
    handleClose,
    handleClear,
    handleButtonClick,
    handleKeyDown,
    handleStepImageLoad,
    handleZoneButtonClick,
    handleStrokeWidthChange,
    handleDoodleClear,

    // Editor handlers
    ...editorHandlers,

    // Setters
    setScale,
    setActiveZoneIndex,

    // Computed values
    buttonClasses,
    zoneButtonClasses,

    // Overlay image styling
    overlayStyle
  };
};
